import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './CoursePage.css';

const CoursePage = () => {
  const { courseId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedModules, setExpandedModules] = useState({});
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [progress, setProgress] = useState({});
  const [materials, setMaterials] = useState([]);

  useEffect(() => {
    loadCourseData();
  }, [courseId]);

  useEffect(() => {
    if (selectedLesson) {
      loadLessonMaterials(selectedLesson.id);
    }
  }, [selectedLesson]);

  const loadCourseData = async () => {
    try {
      const courseRes = await api.get(`/courses/${courseId}`);
      setCourse(courseRes.data);

      const modulesRes = await api.get(`/courses/${courseId}/modules`);
      setModules(modulesRes.data);

      try {
        const enrollmentRes = await api.get(`/enrollments/my-enrollments`);
        const myEnrollment = enrollmentRes.data.find(e => e.course_id === parseInt(courseId));
        setEnrollment(myEnrollment);
      } catch (error) {
        console.log('Não matriculado');
      }

      // Carregar progresso
      try {
        const progressRes = await api.get(`/progress/course/${courseId}`);
        const progressMap = {};
        progressRes.data.lessons.forEach(l => {
          progressMap[l.lesson_id] = l;
        });
        setProgress(progressMap);
      } catch (error) {
        console.log('Erro ao carregar progresso');
      }

      if (modulesRes.data.length > 0) {
        setExpandedModules({ [modulesRes.data[0].id]: true });
        if (modulesRes.data[0].lessons && modulesRes.data[0].lessons.length > 0) {
          setSelectedLesson(modulesRes.data[0].lessons[0]);
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar curso:', error);
      alert('Erro ao carregar curso');
      navigate('/dashboard');
    }
  };

  const loadLessonMaterials = async (lessonId) => {
    try {
      const res = await api.get(`/materials/lesson/${lessonId}`);
      setMaterials(res.data);
    } catch (error) {
      console.log('Erro ao carregar materiais');
      setMaterials([]);
    }
  };

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const handleLessonClick = async (lesson) => {
    if (!enrollment) {
      alert('Você precisa se matricular neste curso primeiro!');
      return;
    }
    if (!lesson.is_free && enrollment.payment_status !== 'approved') {
      alert('Complete o pagamento para acessar esta aula!');
      return;
    }
    
    setSelectedLesson(lesson);
  };

  const handleVideoProgress = async (currentTime) => {
    if (!selectedLesson || !enrollment) return;

    try {
      await api.post(`/progress/lesson/${selectedLesson.id}`, {
        video_progress_seconds: Math.floor(currentTime),
        completed: false
      });
    } catch (error) {
      console.log('Erro ao salvar progresso');
    }
  };

  const handleLessonComplete = async () => {
    if (!selectedLesson || !enrollment) return;

    try {
      await api.post(`/progress/lesson/${selectedLesson.id}/complete`);
      
      // Atualizar progresso local
      setProgress(prev => ({
        ...prev,
        [selectedLesson.id]: {
          ...prev[selectedLesson.id],
          completed: true,
          completed_at: new Date()
        }
      }));

      alert('✅ Aula concluída!');
    } catch (error) {
      console.error('Erro ao marcar como concluída:', error);
    }
  };

  const handleMaterialDownload = async (materialId) => {
    try {
      await api.post(`/materials/${materialId}/download`);
    } catch (error) {
      console.log('Erro ao registrar download');
    }
  };

  const getVideoEmbedUrl = (url) => {
    if (!url) return null;
    
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.split('v=')[1]?.split('&')[0] || url.split('/').pop();
      return `https://www.youtube.com/embed/${videoId}?enablejsapi=1`;
    }
    
    if (url.includes('vimeo.com')) {
      const videoId = url.split('/').pop();
      return `https://player.vimeo.com/video/${videoId}`;
    }
    
    return url;
  };

  if (loading) {
    return <div className="loading">Carregando curso...</div>;
  }

  if (!course) {
    return <div className="error">Curso não encontrado</div>;
  }

  const embedUrl = selectedLesson ? getVideoEmbedUrl(selectedLesson.video_url) : null;
  const isVideo = embedUrl && (embedUrl.includes('youtube') || embedUrl.includes('vimeo'));
  const lessonProgress = selectedLesson ? progress[selectedLesson.id] : null;

  return (
    <div className="course-page">
      <div className="course-header">
        <button onClick={() => navigate('/dashboard')} className="back-button">
          ← Voltar ao Dashboard
        </button>
        <div className="course-title-section">
          <h1>{course.title}</h1>
          <p>{course.description}</p>
          <div className="course-meta">
            <span>👤 {course.instructor_name}</span>
            <span>📚 {modules.length} módulos</span>
            <span>⏱️ {course.duration_hours}h</span>
            {enrollment && <span className="enrolled-badge">✓ Matriculado</span>}
          </div>
        </div>
      </div>

      <div className="course-content-grid">
        <div className="video-section">
          {selectedLesson ? (
            <>
              <div className="video-player">
                {embedUrl ? (
                  isVideo ? (
                    <VideoPlayer 
                      url={embedUrl} 
                      title={selectedLesson.title}
                      onProgress={handleVideoProgress}
                      startTime={lessonProgress?.video_progress_seconds || 0}
                    />
                  ) : (
                    <video controls>
                      <source src={embedUrl} type="video/mp4" />
                      Seu navegador não suporta vídeo.
                    </video>
                  )
                ) : (
                  <div className="no-video">📹 Vídeo em breve</div>
                )}
              </div>

              <div className="lesson-details">
                <div className="lesson-header">
                  <div>
                    <h2>{selectedLesson.title}</h2>
                    <p>{selectedLesson.description}</p>
                  </div>
                  {enrollment && !lessonProgress?.completed && (
                    <button onClick={handleLessonComplete} className="btn-complete">
                      ✓ Marcar como concluída
                    </button>
                  )}
                  {lessonProgress?.completed && (
                    <span className="completed-badge">✅ Concluída</span>
                  )}
                </div>

                {/* Materiais Complementares */}
                {materials.length > 0 && (
                  <div className="materials-section">
                    <h3>📄 Materiais Complementares</h3>
                    <div className="materials-list">
                      {materials.map(material => (
                        
                          <a key={material.id}
                          href={material.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="material-item"
                          onClick={() => handleMaterialDownload(material.id)}
                        >
                          <div className="material-icon">
                            {material.file_type === 'pdf' && '📕'}
                            {material.file_type === 'doc' && '📘'}
                            {material.file_type === 'zip' && '📦'}
                          </div>
                          <div className="material-info">
                            <span className="material-title">{material.title}</span>
                            {material.description && (
                              <span className="material-desc">{material.description}</span>
                            )}
                            <span className="material-meta">
                              {material.file_size_mb && `${material.file_size_mb} MB`}
                              {material.downloads > 0 && ` • ${material.downloads} downloads`}
                            </span>
                          </div>
                          <span className="material-download">⬇️ Download</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="no-lesson-selected">
              <h2>👈 Selecione uma aula</h2>
            </div>
          )}
        </div>

        <div className="modules-sidebar">
          <h3>Conteúdo do Curso</h3>
          {modules.map((module, index) => (
            <div key={module.id} className="module-item">
              <div 
                className="module-header"
                onClick={() => toggleModule(module.id)}
              >
                <span className="module-number">Módulo {index + 1}</span>
                <h4>{module.title}</h4>
                <span className="module-toggle">{expandedModules[module.id] ? '▼' : '▶'}</span>
              </div>
              
              {expandedModules[module.id] && module.lessons && (
                <div className="lessons-list">
                  {module.lessons.map((lesson, lessonIndex) => {
                    const isCompleted = progress[lesson.id]?.completed;
                    return (
                      <div
                        key={lesson.id}
                        className={`lesson-item ${selectedLesson?.id === lesson.id ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                        onClick={() => handleLessonClick(lesson)}
                      >
                        <span className="lesson-number">
                          {isCompleted ? '✓' : lessonIndex + 1}
                        </span>
                        <div className="lesson-info">
                          <span className="lesson-title">{lesson.title}</span>
                          <div className="lesson-meta">
                            <span>🎥 {lesson.duration_minutes}min</span>
                            {lesson.is_free && <span className="free-badge">GRÁTIS</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Componente de Video Player com callback de progresso
const VideoPlayer = ({ url, title, onProgress, startTime }) => {
  useEffect(() => {
    let interval;
    const iframe = document.querySelector('.video-player iframe');
    
    if (iframe && url.includes('youtube')) {
      // Para YouTube, usar postMessage API
      interval = setInterval(() => {
        iframe.contentWindow.postMessage('{"event":"command","func":"getCurrentTime","args":""}', '*');
      }, 5000); // Salvar progresso a cada 5 segundos

      window.addEventListener('message', (event) => {
        if (event.data && typeof event.data === 'string') {
          try {
            const data = JSON.parse(event.data);
            if (data.event === 'infoDelivery' && data.info?.currentTime) {
              onProgress(data.info.currentTime);
            }
          } catch (e) {}
        }
      });
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [url, onProgress]);

  return (
    <iframe
      src={`${url}&start=${Math.floor(startTime)}`}
      title={title}
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    ></iframe>
  );
};

export default CoursePage;
